import { z } from 'zod';
import { uuidSchema, notesSchema, paginationSchema } from './common.js';

/**
 * Spool location types
 */
export const SPOOL_LOCATION_TYPES = ['PRINTER', 'STORAGE', 'NONE'] as const;

/**
 * Spool statuses
 */
export const SPOOL_STATUSES = ['IN_STORAGE', 'LOADED', 'IN_USE', 'EMPTY', 'DISCARDED'] as const;

/**
 * Create a new spool
 */
export const createSpoolSchema = z.object({
  tenantFilamentId: uuidSchema,

  // Purchase Info (all optional)
  purchaseDate: z.coerce.date().nullable().optional(),
  purchasePrice: z.number().min(0).max(10000).nullable().optional(),
  purchaseUrl: z.string().url().max(500).nullable().optional(),

  // Weight Tracking
  initialWeight: z.number().positive().max(10000), // grams
  remainingWeight: z.number().min(0).max(10000), // grams
  emptySpoolWeight: z.number().min(0).max(500).nullable().optional(), // grams

  // Location
  locationType: z.enum(SPOOL_LOCATION_TYPES).default('NONE'),
  tenantPrinterId: uuidSchema.nullable().optional(),
  storageLocationId: uuidSchema.nullable().optional(),

  status: z.enum(SPOOL_STATUSES).default('IN_STORAGE'),
  notes: notesSchema,
});

export type CreateSpoolInput = z.infer<typeof createSpoolSchema>;

/**
 * Update an existing spool
 */
export const updateSpoolSchema = createSpoolSchema.omit({ tenantFilamentId: true }).partial();

export type UpdateSpoolInput = z.infer<typeof updateSpoolSchema>;

/**
 * Move spool to a new location
 */
export const moveSpoolSchema = z.object({
  locationType: z.enum(SPOOL_LOCATION_TYPES),
  tenantPrinterId: uuidSchema.nullable().optional(),
  storageLocationId: uuidSchema.nullable().optional(),
}).refine((data) => {
  // Validate that the correct ID is provided based on location type
  if (data.locationType === 'PRINTER' && !data.tenantPrinterId) {
    return false;
  }
  if (data.locationType === 'STORAGE' && !data.storageLocationId) {
    return false;
  }
  return true;
}, {
  message: 'Must provide tenantPrinterId for PRINTER location or storageLocationId for STORAGE location',
});

export type MoveSpoolInput = z.infer<typeof moveSpoolSchema>;

/**
 * Quick weight update for a spool
 */
export const updateSpoolWeightSchema = z.object({
  remainingWeight: z.number().min(0).max(10000),
});

export type UpdateSpoolWeightInput = z.infer<typeof updateSpoolWeightSchema>;

/**
 * Update spool status
 */
export const updateSpoolStatusSchema = z.object({
  status: z.enum(SPOOL_STATUSES),
});

export type UpdateSpoolStatusInput = z.infer<typeof updateSpoolStatusSchema>;

/**
 * Query/filter schema for spools
 */
export const spoolQuerySchema = paginationSchema.extend({
  tenantFilamentId: uuidSchema.optional(),
  filamentId: uuidSchema.optional(), // Filter by underlying filament
  status: z.enum(SPOOL_STATUSES).optional(),
  locationType: z.enum(SPOOL_LOCATION_TYPES).optional(),
  tenantPrinterId: uuidSchema.optional(),
  storageLocationId: uuidSchema.optional(),
  sortBy: z.enum(['createdAt', 'remainingWeight', 'status', 'purchaseDate']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

export type SpoolQuery = z.infer<typeof spoolQuerySchema>;
